import { useMemo } from 'react';

import { Calculation } from '@work4all/models/lib/Classes/Calculation.entity';
import { SortDirection } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { EntityTableV8, IEntityTableV8 } from './entity-table/EntityTableV8';
import { useEntityDataTableV8 } from './entity-table/use-entity-data-table-v8';
import { ErpRessourceClassCell } from './ErpRessourceClassCell';
import { useErpTable } from './offers-table/erp-common/use-erp-table';
import { useCloneConvertErpActions } from './offers-table/hooks/use-clone-convert-erp';
import { useErpPlanningColumns } from './planning-columns/use-erp-planning-columns';
import schema from './schemata/calculation-table-schema.json';
import { useErpHandlersTemplateV8 } from './use-erp-handlers-template-v8';

const defaultSort = [{ field: 'date', direction: SortDirection.DESCENDING }];

const secondarySort = [
  { field: 'number', direction: SortDirection.DESCENDING },
];

export function CalculationsTable(props: IEntityTableV8) {
  const ressourceClassColumns = useErpPlanningColumns({
    entity: Entities.calculation,
  });

  const transformedSchema = useMemo(() => {
    return {
      ...schema,
      columns: [...schema.columns, ...ressourceClassColumns],
    } as never;
  }, [ressourceClassColumns]);

  const dataTable = useEntityDataTableV8<Calculation, never>({
    ...props,
    schema: transformedSchema,
    cells: {
      ErpRessourceClass: ErpRessourceClassCell,
    },
    defaultSort,
    secondarySort,
    enableFooter: true,
    forceRequestFields: {
      positionList: [
        {
          id: null,
          unit: null,
          amount: null,
          article: {
            id: null,
            name: null,
            number: null,
            ressourceClassId: null,
          },
        },
      ],
    } as Calculation,
    rowModifiers: (value) => {
      return {
        isFaded: value.isClosed && value.frozen,
        isShade1: !value.isClosed && value.frozen,
      };
    },
  });

  const template = useErpHandlersTemplateV8(dataTable.table);

  const { actions } = useCloneConvertErpActions({
    entity: Entities.calculation,
    data: dataTable.selectedEntities,
  });

  const custom = useMemo(
    () => ({
      left: actions,
    }),
    [actions]
  );
  useErpTable(Entities.calculation, dataTable.data.length === 0);

  return (
    <EntityTableV8
      {...props}
      {...dataTable}
      template={template}
      actions={{ custom }}
      displayFooter
    />
  );
}
